/**
 * CoW Protocol client for Gnosis Chain — the venue that CAN swap GBPe
 * (solver liquidity; AMM aggregators have no GBPe route — verified live
 * 2026-08-11: 10 GBPe → 13.51 USDC.e at market rate).
 *
 * Orders are signed EIP-712 and settled gaslessly by solvers; the only
 * on-chain tx we pay for is the one-off ERC-20 approval to the vault relayer.
 *
 * Canonical CoW addresses (identical on every chain; the API rejects the
 * signature if they were ever wrong, so a mismatch fails loudly, not
 * silently):
 *   settlement   0x9008D19f58AAbD9eD0D60971565AA8510560ab41
 *   vaultRelayer 0xC92E8bdf79f0507f65a392b0ab4667716BFE0110
 */
import { concatHex, erc20Abi, hashTypedData, toHex } from "viem";
import { gnosisPublicClient, walletClientFor } from "./chains";
import type { LocalWallet } from "./wallet";
import type { Hex } from "./types";

const API = "https://api.cow.fi/xdai/api/v1";
const SETTLEMENT = "0x9008D19f58AAbD9eD0D60971565AA8510560ab41" as Hex;
const VAULT_RELAYER = "0xC92E8bdf79f0507f65a392b0ab4667716BFE0110" as Hex;
const SLIPPAGE_BPS = 50n; // 0.5%

const ORDER_TYPES = {
  Order: [
    { name: "sellToken", type: "address" },
    { name: "buyToken", type: "address" },
    { name: "receiver", type: "address" },
    { name: "sellAmount", type: "uint256" },
    { name: "buyAmount", type: "uint256" },
    { name: "validTo", type: "uint32" },
    { name: "appData", type: "bytes32" },
    { name: "feeAmount", type: "uint256" },
    { name: "kind", type: "string" },
    { name: "partiallyFillable", type: "bool" },
    { name: "sellTokenBalance", type: "string" },
    { name: "buyTokenBalance", type: "string" },
  ],
} as const;

interface CowQuote {
  sellToken: Hex;
  buyToken: Hex;
  receiver: Hex | null;
  sellAmount: string;
  buyAmount: string;
  validTo: number;
  appData: Hex;
  feeAmount: string;
  kind: string;
  partiallyFillable: boolean;
  sellTokenBalance: string;
  buyTokenBalance: string;
}

export class CowError extends Error {}

export interface CowOrderSubmission {
  orderUid: Hex;
  minBuy: bigint;
  validTo: number;
}

export async function getCowQuote(
  sellToken: Hex,
  buyToken: Hex,
  sellUnits: bigint,
  from: Hex,
  receiver?: Hex,
): Promise<CowQuote> {
  const res = await fetch(`${API}/quote`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      sellToken,
      buyToken,
      sellAmountBeforeFee: sellUnits.toString(),
      kind: "sell",
      from,
      ...(receiver ? { receiver } : {}),
    }),
    signal: AbortSignal.timeout(20_000),
  });
  if (!res.ok) throw new CowError(`CoW quote failed: ${(await res.text()).slice(0, 200)}`);
  const json = await res.json();
  return json.quote as CowQuote;
}

/**
 * Approve (exact amount) if needed, sign the order, post it, and poll until
 * solvers fill it. Returns the buy-token amount actually received.
 */
export async function executeCowSwap(
  wallet: LocalWallet,
  sellToken: Hex,
  buyToken: Hex,
  sellUnits: bigint,
  receiver: Hex,
  onProgress?: (label: string) => void,
  onOrderPrepared?: (submission: CowOrderSubmission) => Promise<void> | void,
): Promise<bigint> {
  const publicClient = gnosisPublicClient();

  const quote = await getCowQuote(sellToken, buyToken, sellUnits, wallet.address, receiver);
  // Fee model (CoW orderbook OpenAPI, verified 2026-08-11): quotes still
  // report an estimated network feeAmount, but orders MUST be created with
  // feeAmount = 0 — "fees are now computed dynamically by solvers". So the
  // order sells the FULL amount (quote sellAmount + quoted fee) and the
  // solver's fee comes out of the spread above our limit price.
  const sellAmount = BigInt(quote.sellAmount) + BigInt(quote.feeAmount);
  const minBuy = (BigInt(quote.buyAmount) * (10_000n - SLIPPAGE_BPS)) / 10_000n;
  // Expire in 15 minutes: an unfilled order dies on its own, so an abandoned
  // attempt can never fill later behind the user's back.
  const validTo = Math.floor(Date.now() / 1000) + 15 * 60;

  // One-off exact-amount approval to the vault relayer (the only gas we pay).
  const needed = sellAmount;
  const allowance = await publicClient.readContract({
    address: sellToken,
    abi: erc20Abi,
    functionName: "allowance",
    args: [wallet.address, VAULT_RELAYER],
  });
  if (allowance < needed) {
    onProgress?.("Approving token…");
    const walletClient = walletClientFor(100, wallet.account);
    const hash = await walletClient.writeContract({
      address: sellToken,
      abi: erc20Abi,
      functionName: "approve",
      args: [VAULT_RELAYER, needed],
      chain: walletClient.chain,
      account: wallet.account,
    });
    await publicClient.waitForTransactionReceipt({ hash, timeout: 120_000 });
  }

  const order = {
    sellToken,
    buyToken,
    receiver,
    sellAmount: sellAmount.toString(),
    buyAmount: minBuy.toString(),
    validTo,
    appData: quote.appData,
    feeAmount: "0",
    kind: quote.kind,
    partiallyFillable: false,
    sellTokenBalance: quote.sellTokenBalance ?? "erc20",
    buyTokenBalance: quote.buyTokenBalance ?? "erc20",
  };

  onProgress?.("Signing order…");
  const typedData = {
    domain: { name: "Gnosis Protocol", version: "v2", chainId: 100, verifyingContract: SETTLEMENT },
    types: ORDER_TYPES,
    primaryType: "Order",
    message: {
      ...order,
      sellAmount: BigInt(order.sellAmount),
      buyAmount: BigInt(order.buyAmount),
      feeAmount: BigInt(order.feeAmount),
    },
  } as const;
  const signature = await wallet.account.signTypedData(typedData);
  // CoW's UID is deterministic: digest (32) + owner (20) + validTo (4).
  // Persisting it before POST closes the crash window where an accepted order
  // could otherwise be submitted again after a PWA restart.
  const orderUid = concatHex([
    hashTypedData(typedData),
    wallet.address,
    toHex(validTo, { size: 4 }),
  ]);
  await onOrderPrepared?.({ orderUid, minBuy, validTo });

  const res = await fetch(`${API}/orders`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ ...order, signingScheme: "eip712", signature, from: wallet.address }),
    signal: AbortSignal.timeout(20_000),
  });
  if (!res.ok) {
    // A retry after losing the original HTTP response is safe when the exact
    // deterministic UID already exists in the orderbook.
    const existing = await fetch(`${API}/orders/${orderUid}`, {
      signal: AbortSignal.timeout(15_000),
    }).catch(() => null);
    if (!existing?.ok) throw new CowError(`CoW order rejected: ${(await res.text()).slice(0, 200)}`);
  } else {
    const returnedUid = (await res.json()) as string;
    if (returnedUid.toLowerCase() !== orderUid.toLowerCase()) {
      throw new CowError("CoW returned an unexpected order identifier");
    }
  }

  onProgress?.("Waiting for solvers…");
  return waitCowOrder(orderUid, minBuy, validTo);
}

/** Resolve a previously prepared/submitted order without posting it again. */
export async function waitCowOrder(
  orderUid: Hex,
  minBuy: bigint,
  validTo: number,
): Promise<bigint> {
  // Poll until the order's own expiry (validTo) + grace: it can fill any
  // time until then, and past validTo it is dead — no orphaned open order.
  const deadline = validTo * 1000 + 30_000;
  for (;;) {
    await new Promise((r) => setTimeout(r, 4_000));
    const st = await fetch(`${API}/orders/${orderUid}`, { signal: AbortSignal.timeout(15_000) });
    if (!st.ok) continue;
    const o = await st.json();
    if (o.status === "fulfilled") return BigInt(o.executedBuyAmount ?? minBuy);
    if (o.status === "cancelled" || o.status === "expired") {
      throw new CowError(`CoW order ${o.status}`);
    }
    if (Date.now() > deadline) {
      throw new CowError("CoW order not filled before it expired — funds untouched, safe to retry");
    }
  }
}
